import { useEffect, useState, FormEvent } from 'react';
import { useToast } from '@sectepe/ui';
import {
  PlatformCredential,
  PlatformCredentialStatus,
  listPlatformCredentials,
  setPlatformCredential,
  clearPlatformCredential,
  verifyPlatformCredential,
  extractApiError,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { PLATFORM_CREDENTIAL_STATUS_LABEL } from '../services/status-labels';

/**
 * Seite „Zugangsdaten“: externe API-Zugaenge (Core-Networks-DNS, IONOS,
 * IMAP-Archiv, Kimai, AD-Bind, Pulp-GPG …), die die Plattform selbst nicht
 * erzeugen kann. Die Werte landen ueber platform-admin in OpenBao an den
 * Pfaden, die ExternalSecrets, Seed-CronJob und AWX bereits lesen; das
 * Frontend sieht geheime Werte nie, nur ob sie gesetzt sind.
 */

const STATUS_COLORS: Record<PlatformCredentialStatus, string> = {
  complete: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  missing: 'bg-red-100 text-red-700',
  unknown: 'bg-slate-100 text-slate-500',
};

/**
 * Reduziert die Formulareingabe auf das, was wirklich geschrieben werden
 * soll: geheime Felder nur, wenn etwas eingetippt wurde (leer = unveraendert
 * lassen); nicht geheime Felder nur bei Aenderung gegenueber dem Ist-Wert.
 * Leerer String bei einem nicht geheimen Feld loescht es.
 */
export function buildChangeSet(
  cred: Pick<PlatformCredential, 'fields'>,
  draft: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of cred.fields) {
    const typed = draft[f.key] ?? '';
    if (f.secret) {
      if (typed.length > 0) out[f.key] = typed;
    } else if (typed !== (f.value ?? '')) {
      out[f.key] = typed;
    }
  }
  return out;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE');
}

export function PlatformCredentialsPage() {
  const toast = useToast();
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<PlatformCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  function refresh(): void {
    setLoading(true);
    setError(null);
    listPlatformCredentials()
      .then((r) => {
        setRows(r);
        setNotConfigured(false);
      })
      .catch((err) => {
        const e = err as { response?: { status?: number; data?: { code?: string } } };
        if (e.response?.status === 503 && e.response.data?.code === 'credentials_not_configured') {
          setNotConfigured(true);
        } else {
          setError(extractApiError(err));
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  function replaceRow(next: PlatformCredential): void {
    setRows((prev) => prev.map((r) => (r.id === next.id ? next : r)));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Zugangsdaten</h2>
          <p className="text-sm text-slate-500">
            Externe API-Zugänge, die die Plattform nicht selbst erzeugen kann (Registrar, DNS-Provider,
            Fremdsysteme). Werte werden direkt nach OpenBao geschrieben; ExternalSecrets, Seed-Job und AWX
            greifen sie von dort ab. Geheime Werte werden hier nie angezeigt.
          </p>
        </div>
        <button onClick={refresh} className="btn-secondary" disabled={loading}>
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>
      )}

      {notConfigured && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 mb-4 text-sm">
          Diese Instanz hat keinen OpenBao-Zugang (gescopte Enterprise-Instanz). Zugangsdaten werden nur
          auf der zentralen Operator-Konsole verwaltet.
        </div>
      )}

      {loading && rows.length === 0 && !notConfigured && (
        <p className="text-sm text-slate-500">Lade …</p>
      )}

      <div className="space-y-4">
        {rows.map((c) => (
          <CredentialCard
            key={c.id}
            cred={c}
            readOnly={isReadOnly}
            onChanged={replaceRow}
            onToast={(message, severity) => toast.push({ message, severity })}
          />
        ))}
      </div>
    </div>
  );
}

interface CardProps {
  cred: PlatformCredential;
  readOnly: boolean;
  onChanged: (next: PlatformCredential) => void;
  onToast: (message: string, severity: 'success' | 'error' | 'info') => void;
}

function CredentialCard({ cred, readOnly, onChanged, onToast }: CardProps) {
  const [open, setOpen] = useState(cred.status !== 'complete');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'save' | 'clear' | 'verify' | null>(null);
  const [verifyMsg, setVerifyMsg] = useState<{ ok: boolean; message: string } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // Nicht geheime Felder mit Ist-Wert vorbelegen, sobald der Server-Stand wechselt.
  useEffect(() => {
    const init: Record<string, string> = {};
    for (const f of cred.fields) init[f.key] = f.secret ? '' : (f.value ?? '');
    setDraft(init);
  }, [cred]);

  const changes = buildChangeSet(cred, draft);
  const dirty = Object.keys(changes).length > 0;

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!dirty) return;
    setBusy('save');
    setVerifyMsg(null);
    try {
      const next = await setPlatformCredential(cred.id, changes);
      onChanged(next);
      onToast(`${cred.label}: gespeichert.`, 'success');
    } catch (err) {
      onToast(`${cred.label}: ${extractApiError(err)}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  async function clearAll(): Promise<void> {
    setBusy('clear');
    setVerifyMsg(null);
    try {
      const next = await clearPlatformCredential(cred.id);
      onChanged(next);
      setConfirmClear(false);
      onToast(`${cred.label}: Werte gelöscht.`, 'info');
    } catch (err) {
      onToast(`${cred.label}: ${extractApiError(err)}`, 'error');
    } finally {
      setBusy(null);
    }
  }

  async function verify(): Promise<void> {
    setBusy('verify');
    try {
      const r = await verifyPlatformCredential(cred.id);
      setVerifyMsg(r);
    } catch (err) {
      setVerifyMsg({ ok: false, message: extractApiError(err) });
    } finally {
      setBusy(null);
    }
  }

  const anySet = cred.fields.some((f) => f.set);

  return (
    <div className="bg-white shadow rounded">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[cred.status]}`}>
            {PLATFORM_CREDENTIAL_STATUS_LABEL[cred.status]}
          </span>
          <span className="font-semibold truncate">{cred.label}</span>
          <span className="text-xs text-slate-400 font-mono truncate hidden md:inline">{cred.openbaoPath}</span>
        </div>
        <span className="text-slate-400 text-sm">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t px-4 py-4 space-y-4">
          <p className="text-sm text-slate-600">{cred.description}</p>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Verbraucher</dt>
              <dd className="text-slate-700">{cred.consumers.join(', ')}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Wirkung nach dem Speichern</dt>
              <dd className="text-slate-700">{cred.effect}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-400">Zuletzt geändert</dt>
              <dd className="text-slate-700">
                {formatDate(cred.updatedAt)}
                {cred.updatedBy ? ` von ${cred.updatedBy}` : ''}
              </dd>
            </div>
            {cred.docs && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-400">Dokumentation</dt>
                <dd className="text-slate-700 font-mono text-xs">{cred.docs}</dd>
              </div>
            )}
          </dl>

          {cred.error && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-2 text-xs">
              OpenBao nicht lesbar: {cred.error}
            </div>
          )}

          <form onSubmit={save} className="space-y-3">
            {cred.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium mb-1" htmlFor={`${cred.id}-${f.key}`}>
                  {f.label}
                  {f.required && <span className="text-red-500"> *</span>}
                  <span
                    className={`ml-2 text-xs font-normal ${f.set ? 'text-green-700' : 'text-slate-400'}`}
                  >
                    {f.set ? 'gesetzt' : 'nicht gesetzt'}
                  </span>
                </label>
                <input
                  id={`${cred.id}-${f.key}`}
                  type={f.secret ? 'password' : 'text'}
                  autoComplete={f.secret ? 'new-password' : 'off'}
                  value={draft[f.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.secret && f.set ? '•••••••• (leer lassen = unverändert)' : f.placeholder ?? ''}
                  disabled={readOnly || busy !== null}
                  className="w-full px-2 py-1 rounded border text-sm font-mono"
                  spellCheck={false}
                />
                {f.hint && <p className="text-xs text-slate-500 mt-1">{f.hint}</p>}
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button type="submit" className="btn-primary" disabled={readOnly || !dirty || busy !== null}>
                {busy === 'save' ? 'Speichere …' : 'Speichern'}
              </button>
              {cred.verifiable && (
                <button
                  type="button"
                  onClick={verify}
                  className="btn-secondary"
                  disabled={readOnly || busy !== null || !anySet}
                >
                  {busy === 'verify' ? 'Prüfe …' : 'Verbindung prüfen'}
                </button>
              )}
              {!confirmClear ? (
                <button
                  type="button"
                  onClick={() => setConfirmClear(true)}
                  className="text-sm text-red-600 hover:underline ml-auto"
                  disabled={readOnly || busy !== null || !anySet}
                >
                  Werte löschen
                </button>
              ) : (
                <span className="ml-auto flex items-center gap-2 text-sm">
                  <span className="text-red-700">Alle Felder in OpenBao leeren?</span>
                  <button type="button" onClick={clearAll} className="text-red-700 font-medium hover:underline" disabled={busy !== null}>
                    {busy === 'clear' ? 'Lösche …' : 'Ja, löschen'}
                  </button>
                  <button type="button" onClick={() => setConfirmClear(false)} className="text-slate-500 hover:underline" disabled={busy !== null}>
                    Abbrechen
                  </button>
                </span>
              )}
            </div>
          </form>

          {verifyMsg && (
            <div
              className={`rounded p-2 text-sm border ${
                verifyMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
              }`}
            >
              {verifyMsg.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
